import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { useMutation } from 'react-query';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { GoggleApi } from '@jwp/ott-common/src/env';
import { PENDING_LIBRARY_SELECTION_KEY, SOCCIAL_LOGIN_OPENATHENS } from '@jwp/ott-common/src/constants';
import { type LibraryBranch, type LibraryItem } from '@jwp/ott-common/src/controllers/graphql';
import { fetchUserLocation } from '@jwp/ott-common/src/controllers/graphql';
import { getSiteType } from '../../utils/useSiteType';

import icon from '../../../../theme/assets/icons/Icon.svg';
import defaultIcon from '../../../../theme/assets/icons/default.svg';

import styles from './Mapview.module.scss';

interface WindowWithGoogle extends Window {
  google: any;
}

type Props = {
  libraryLists?: LibraryItem[];
  seleteLocation?: any;
  selectLibraryButtonLabel?: string;
  /** When provided (e.g. from LibraryAutocomplete), matches list “Select library” routing (public / academic / OpenAthens). */
  onLibraryBranchSelect?: (branch: LibraryBranch, libraryItem: LibraryItem) => void;
};

const escapeForInfoWindowText = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const GoogleMap: React.FC<Props> = ({ libraryLists, seleteLocation, selectLibraryButtonLabel, onLibraryBranchSelect }) => {
  const onLibraryBranchSelectRef = useRef(onLibraryBranchSelect);
  onLibraryBranchSelectRef.current = onLibraryBranchSelect;
  const { t } = useTranslation('account');
  const selectLabel = escapeForInfoWindowText(selectLibraryButtonLabel ?? t('Library_Access.Library_auto_complete.map_select_library'));
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const clickedIconUrl = icon;
  const navigate = useNavigate();
  const location = useLocation();
  const storedLocation = sessionStorage.getItem('userLocation');
  const { state } = location;

  const { libraryListData, locationRef } = state ?? JSON.parse(storedLocation as any);
  const [userLocation, setUserLocation] = useState({
    latitude: 0,
    longitude: 0,
  });

  const currentInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const LocationMutation = useMutation({
    mutationFn: fetchUserLocation,
    onError: (error) => {
      console.error('LocationMutation error:', error);
    },
    onSuccess: (data: any) => {
      if (data) {
        const { longitude, latitude } = data.GetLocationByIP;
        setUserLocation({ latitude, longitude });
      }
    },
  });

  useEffect(() => {
    LocationMutation.mutate();
  }, []);

  useEffect(() => {
    if (!userLocation.latitude || !userLocation.longitude) return;

    const loader = new Loader({
      apiKey: GoggleApi.API_KEY,
      version: 'weekly',
    });

    loader.load().then(() => {
      const google = (window as WindowWithGoogle).google;

      const selectedLat = seleteLocation?.current?.libraryLat || locationRef?.current?.libraryLat || Number(userLocation.latitude);
      const selectedLon = seleteLocation?.current?.libraryLon || locationRef?.current?.libraryLon || Number(userLocation.longitude);

      const map = new google.maps.Map(mapRef.current, {
        center: {
          lat: selectedLat,
          lng: selectedLon,
        },
        zoom: 8,
        panControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        disableDefaultUI: true,
        scrollWheel: true,
        draggable: true,
        zoomControl: true,
        fullscreenControl: false,
      });

      mapInstanceRef.current = map;

      const markers: google.maps.Marker[] = [];

      const libraries = libraryLists || libraryListData;
      if (libraries) {
        libraries.forEach((param: LibraryItem) => {
          param.libraryBranch.forEach((branch: LibraryBranch) => {
            const marker = new google.maps.Marker({
              position: { lat: branch.libraryLat, lng: branch.libraryLon },
              map: map,
              icon: {
                url: defaultIcon,
                scaledSize: new google.maps.Size(30, 30),
              },
            });

            markers.push(marker);

            const infoWindow = new google.maps.InfoWindow({
              content: `
              <style>
                .gm-style-iw { padding: 0 !important; }
                .gm-ui-hover-effect { display: none !important; }
                .gm-style-iw-ch { padding: 0 !important; }
                .gm-style-iw-c { padding: 0 !important; max-height: none !important; }
                .gm-style-iw-d { overflow: visible !important; padding: 0 !important; max-height: none !important; }
                .gm-style-iw-tc { padding-bottom: 0 !important; }
                .custom-info-window { font-family: Arial, sans-serif; width: 260px; margin: 0; padding: 0; line-height: 1.3; box-sizing: border-box; }
                .custom-info-window * { box-sizing: border-box; }
                .info-header { display: flex; justify-content: space-between; align-items: center; background-color: #233b2d; color: white; padding: 5px 10px; font-size: 14px; font-weight: bold; gap: 12px; }
                .info-header .headingText { margin: 0; }
                .info-body { padding: 10px 10px 10px 10px; margin: 0; }
                .parentName { color: #233b2d; font-weight: 500; font-size: 1rem; text-transform: capitalize; margin: 0 0 10px 0; }
                .select-library-btn { background-color: #FFBA30; color: white; border-radius: 0; border: none; padding: 8px 16px; cursor: pointer; font-size: 14px; margin: 0; width: 100%; display: block; }
                .close-btn { cursor: pointer; font-size: 18px; color: white; }
                .headingText { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 12px; font-weight: 700; text-transform: capitalize; }
              </style>
              <div class="custom-info-window">
                <div class="info-header">
                  <h4 class="headingText">${param.libraryParent.libraryName.toLowerCase()}</h4>
                  <span class="close-btn">✕</span>
                </div>
                <div class="info-body">
                  <p class="parentName">${branch.libraryName.toLowerCase()}</p>
                  <button class="select-library-btn">${selectLabel}</button>
                </div>
              </div>
            `,
            });

            const openInfoWindow = () => {
              if (currentInfoWindowRef.current) {
                currentInfoWindowRef.current.close();
              }
              infoWindow.open(map, marker);
              currentInfoWindowRef.current = infoWindow;
            };

            marker.addListener('click', openInfoWindow);

            marker.addListener('mouseover', () => {
              marker.setIcon({
                url: clickedIconUrl,
                scaledSize: new google.maps.Size(30, 30),
              });
            });

            marker.addListener('mouseout', () => {
              marker.setIcon({
                url: defaultIcon,
                scaledSize: new google.maps.Size(30, 30),
              });
            });

            infoWindow.addListener('domready', () => {
              const closeButton = document.querySelector('.close-btn');
              closeButton?.addEventListener('click', () => {
                infoWindow.close();
              });

              const selectButton = document.querySelector('.select-library-btn');
              selectButton?.addEventListener('click', () => {
                const parentHandler = onLibraryBranchSelectRef.current;
                if (parentHandler) {
                  parentHandler(branch, param);
                } else {
                  const entityId = param.libraryParent?.openathensEntityId?.trim();
                  if (getSiteType() === 'academic' && entityId) {
                    sessionStorage.removeItem(PENDING_LIBRARY_SELECTION_KEY);
                    globalThis.location.assign(SOCCIAL_LOGIN_OPENATHENS.replace('{openathensEntityId}', entityId));
                  } else {
                    navigate(modalURLFromLocation(location, 'library-access'), {
                      state: {
                        libraryId: branch.libraryId,
                        libraryName: branch.libraryName,
                        libraryParent: param.restriction,
                        withInOrganization: param.withInOrganization,
                        libraryType: branch.libraryType,
                      },
                    });
                  }
                }
                infoWindow.close();
                sessionStorage.removeItem('userLocation');
              });
            });

            const shouldOpenInfoWindow =
              (seleteLocation?.current?.libraryLat === branch?.libraryLat && seleteLocation?.current?.libraryLon === branch?.libraryLon) ||
              (locationRef?.current?.libraryLat === branch?.libraryLat && locationRef?.current?.libraryLon === branch?.libraryLon);

            if (shouldOpenInfoWindow) {
              openInfoWindow();
            }
          });
        });
      }
    });

    return () => {
      if (mapInstanceRef.current) {
        google.maps.event.clearInstanceListeners(mapInstanceRef.current);
      }
    };
  }, [
    userLocation,
    libraryLists,
    seleteLocation?.current?.libraryLat,
    locationRef,
    clickedIconUrl,
    location,
    navigate,
    libraryListData,
    seleteLocation,
    seleteLocation?.current?.libraryLon,
    selectLabel,
  ]);

  useEffect(() => {
    if (mapInstanceRef.current && userLocation.latitude && userLocation.longitude) {
      const newCenter = {
        lat: userLocation.latitude,
        lng: userLocation.longitude,
      };
      mapInstanceRef.current.panTo(newCenter);
    }
  }, [userLocation]);

  return (
    <>
      <div ref={mapRef} className={styles.map} />
    </>
  );
};

export default GoogleMap;
